import axios, { AxiosResponse } from 'axios';
import { ID, Response } from '../../../../../_metronic/helpers';
import {
  Provider,
  ProvidersQueryResponse,
  ProviderAccreditation,
  ProviderAccreditationQueryResponse,
  Clinic,
  ProviderConnectionsQueryResponse,
  IProviderConnection,
  ProviderActivePlans,
} from './_models';
import { CLINIC_PROVIDER_URL, CLINIC_URL, PROVIDER_CONNECTIONS_URL, PROVIDER_URL } from '../../../../helpers/api';
import { monitoringCaptureError } from '../../../../helpers/monitoring';
import qs from 'qs';
import { ISuperBillService } from '../../services/core/_models';

const getProviders = (query: string, onError?: (error?: any) => void): Promise<ProvidersQueryResponse | undefined> => {
  return axios
    .get(`${PROVIDER_URL}?${query}`)
    .then((d: AxiosResponse<ProvidersQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getProviders',
        data: { query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClinicProviders = (
  query: string,
  onError?: (error?: any) => void
): Promise<ProvidersQueryResponse | undefined> => {
  return axios
    .get(`${CLINIC_PROVIDER_URL}?${query}`)
    .then((d: AxiosResponse<ProvidersQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getClinicProviders',
        data: { query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getProviderById = (
  providerUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Provider | undefined> => {
  return axios
    .get(`${PROVIDER_URL}${providerUuid}/?${query}`)
    .then((response: AxiosResponse<Provider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getProviderById',
        data: { providerUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createProvider = (props: {
  provider: Provider;
  options?: any;
  onError?: (error?: any) => void;
}): Promise<Provider | undefined> => {
  const query = props.options ? qs.stringify(props.options, { skipNulls: true }) : '';
  return axios
    .post(`${PROVIDER_URL}?${query}`, props.provider)
    .then((response: AxiosResponse<Provider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'createProvider',
        data: { provider: props.provider, options: props.options },
      });
      if (props.onError) props.onError(error);
      else throw error;

      return undefined;
    });
};

const updateProvider = (
  provider: Provider,
  updates: Provider,
  onError?: (error?: any) => void
): Promise<Provider | undefined> => {
  return axios
    .patch(`${PROVIDER_URL}${provider.uuid}/`, updates)
    .then((response: AxiosResponse<Provider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'updateProvider',
        data: { provider: provider },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getProviderActivePlans = (
  providerUuid: ID,
  onError?: (error?: any) => void
): Promise<ProviderActivePlans | undefined> => {
  return axios
    .get(`${PROVIDER_URL}${providerUuid}/active-plans/`)
    .then((d: AxiosResponse<ProviderActivePlans>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getProviderActivePlans',
        data: { providerUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteProvider = (providerUuid: ID, onError?: (error?: any) => void): Promise<void> => {
  return axios
    .delete(`${PROVIDER_URL}${providerUuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'deleteProvider',
        data: { providerUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteSelectedProviders = (providerUuids: Array<ID>, onError?: (error?: any) => void): Promise<void> => {
  const requests = providerUuids.map((uuid) =>
    axios.delete(`${PROVIDER_URL}${uuid}/`).catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'deleteSelectedProviders',
        data: { uuid, providerUuids },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    })
  );
  return axios.all(requests).then(() => {});
};

const inviteProvider = (provider: Provider, toClinic?: boolean, onError?: (error?: any) => void): Promise<void> => {
  return axios
    .post(`${PROVIDER_URL}${provider.uuid}/invite/`, { to_clinic: toClinic })
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'deleteProviderConnection',
        data: { provider: provider.uuid, toClinic },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getAccreditations = (
  providerUuid: ID,
  query: string,
  onError?: (error?: any) => void
): Promise<ProviderAccreditationQueryResponse | undefined> => {
  return axios
    .get(`${PROVIDER_URL}/${providerUuid}/accreditations?${query}`)
    .then((d: AxiosResponse<ProviderAccreditationQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getAccreditations',
        data: { providerUuid, query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getProviderAccreditationById = (
  providerUuid: ID,
  providerAccreditationUuid: ID,
  onError?: (error?: any) => void
): Promise<ProviderAccreditation | undefined> => {
  return axios
    .get(`${PROVIDER_URL}/${providerUuid}/accreditations/${providerAccreditationUuid}/`)
    .then((response: AxiosResponse<ProviderAccreditation>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'deleteSelectedProviders',
        data: { providerUuid, providerAccreditationUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createProviderAccreditation = (
  providerUuid: ID,
  providerAccreditation: ProviderAccreditation,
  onError?: (error?: any) => void
): Promise<ProviderAccreditation | undefined> => {
  return axios
    .post(`${PROVIDER_URL}${providerUuid}/accreditations/`, providerAccreditation)
    .then((response: AxiosResponse<ProviderAccreditation>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'createProviderAccreditation',
        data: { providerUuid, providerAccreditation },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateProviderAccreditation = (
  providerUuid: ID,
  providerAccreditation: ProviderAccreditation,
  onError?: (error?: any) => void
): Promise<ProviderAccreditation | undefined> => {
  return axios
    .patch(`${PROVIDER_URL}${providerUuid}/accreditations/${providerAccreditation.uuid}/`, providerAccreditation)
    .then((response: AxiosResponse<ProviderAccreditation>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'updateProviderAccreditation',
        data: { providerUuid, providerAccreditation },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createClinic = (clinic: Provider, onError?: (error?: any) => void): Promise<Provider | undefined> => {
  return axios
    .post(CLINIC_URL, clinic)
    .then((response: AxiosResponse<Provider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'createClinic',
        data: { clinic },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateClinic = (clinic: Provider, onError?: (error?: any) => void): Promise<Provider | undefined> => {
  return axios
    .patch(`${CLINIC_URL}${clinic.uuid}/`, clinic)
    .then((response: AxiosResponse<Provider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'updateClinic',
        data: { clinic },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const togglePaymentFailureNotifications = (
  orgUuid: string,
  enabled: boolean,
  onError?: (error?: any) => void
): Promise<{ enabled: boolean } | undefined> => {
  return axios
    .patch(`${PROVIDER_URL}${orgUuid}/payment-failure-notifications/`, { enabled })
    .then((response: AxiosResponse<{ enabled: boolean }>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'togglePaymentFailureNotifications',
        data: { orgUuid, enabled },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getProviderConnections = (
  query: string,
  onError?: (error?: any) => void
): Promise<ProviderConnectionsQueryResponse | undefined> => {
  return axios
    .get(`${PROVIDER_CONNECTIONS_URL}?${query}`)
    .then((d: AxiosResponse<ProviderConnectionsQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getProviderConnections',
        data: { query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createProviderConnection = (
  providerConnection: IProviderConnection,
  onError?: (error?: any) => void
): Promise<IProviderConnection | undefined> => {
  return axios
    .post(PROVIDER_CONNECTIONS_URL, providerConnection)
    .then((response: AxiosResponse<IProviderConnection>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'createProviderConnection',
        data: { providerConnection },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateProviderConnection = (
  providerConnection: IProviderConnection,
  onError?: (error?: any) => void
): Promise<IProviderConnection | undefined> => {
  return axios
    .patch(`${PROVIDER_CONNECTIONS_URL}${providerConnection.uuid}/`, providerConnection)
    .then((response: AxiosResponse<IProviderConnection>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'updateProviderConnection',
        data: { providerConnection },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteProviderConnection = (
  providerConnection: IProviderConnection,
  onError?: (error?: any) => void
): Promise<void> => {
  return axios
    .delete(`${PROVIDER_CONNECTIONS_URL}${providerConnection.uuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'deleteProviderConnection',
        data: { providerConnection },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getConnectionForProvider = (
  providerUuid: string,
  onError?: (error?: any) => void
): Promise<IProviderConnection | undefined> => {
  return axios
    .get(`${PROVIDER_CONNECTIONS_URL}?provider_uuid=${providerUuid}`)
    .then((d: AxiosResponse<ProviderConnectionsQueryResponse>) => {
      const results = d.data?.data || (d.data as any);
      if (Array.isArray(results) && results.length > 0) return results[0];
      return undefined;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getConnectionForProvider',
        data: { providerUuid },
      });
      if (onError) onError(error);
      return undefined;
    });
};

// =========================
// Referral Import
// =========================

export interface ReferralImportResult {
  row: number;
  status: 'matched' | 'created' | 'skipped' | 'error';
  provider_uuid?: string;
  connection_uuid?: string;
  provider_name?: string;
  message?: string;
}

export interface ReferralImportStatus {
  uuid: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_name: string;
  total_records: number;
  matched_count: number;
  created_count: number;
  skipped_count: number;
  error_count: number;
  error_message?: string;
  results: ReferralImportResult[];
  created_at: string;
  updated_at: string;
}

const importReferralList = (file: File): Promise<ReferralImportStatus> => {
  const formData = new FormData();
  formData.append('file', file);
  return axios
    .post(`${PROVIDER_CONNECTIONS_URL}import-referrals/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response: AxiosResponse<ReferralImportStatus>) => response.data);
};

const getReferralImportStatus = (uuid: string): Promise<ReferralImportStatus> => {
  return axios
    .get(`${PROVIDER_CONNECTIONS_URL}import-status/?uuid=${uuid}`)
    .then((response: AxiosResponse<ReferralImportStatus>) => response.data);
};

const getRecentReferralImports = (): Promise<ReferralImportStatus[]> => {
  return axios
    .get(`${PROVIDER_CONNECTIONS_URL}import-status/`)
    .then((response: AxiosResponse<ReferralImportStatus[]>) => response.data);
};

// =========================
// Superbill Services
// =========================
// export type SuperbillServicesQueryResponse = Response<Array<ISuperBillService>>;

const getSuperbillServices = (
  providerUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<ISuperBillService> | undefined> => {
  return axios
    .get(`${PROVIDER_URL}${providerUuid}/superbill-services/?${query}`)
    .then((d) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'getSuperbillServices',
        data: { providerUuid, query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createSuperbillService = (
  providerUuid: ID,
  service: ISuperBillService,
  onError?: (error?: any) => void
): Promise<ISuperBillService | undefined> => {
  return axios
    .post(`${PROVIDER_URL}${providerUuid}/superbill-services/`, service)
    .then((response: AxiosResponse<ISuperBillService>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'createSuperbillService',
        data: { providerUuid, service },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateSuperbillService = (
  providerUuid: ID,
  service: ISuperBillService,
  onError?: (error?: any) => void
): Promise<ISuperBillService | undefined> => {
  return axios
    .patch(`${PROVIDER_URL}${providerUuid}/superbill-services/${service.uuid}/`, service)
    .then((response: AxiosResponse<ISuperBillService>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'updateSuperbillService',
        data: { providerUuid, service },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteSuperbillService = (providerUuid: ID, serviceUuid: ID, onError?: (error?: any) => void): Promise<void> => {
  return axios
    .delete(`${PROVIDER_URL}${providerUuid}/superbill-services/${serviceUuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'providers._requests',
        func: 'deleteSuperbillService',
        data: { providerUuid, serviceUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

// ── Staff Import ───────────────────────────────────────────────────

export interface StaffImportResult {
  row: number;
  status: 'matched' | 'created' | 'skipped' | 'error';
  provider_uuid?: string;
  provider_name?: string;
  message?: string;
}

export interface StaffImportStatus {
  uuid: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  import_type: string;
  file_name: string;
  total_records: number;
  matched_count: number;
  created_count: number;
  skipped_count: number;
  error_count: number;
  error_message?: string;
  results: StaffImportResult[];
  created_at: string;
  updated_at: string;
}

const importStaffList = (file: File): Promise<StaffImportStatus> => {
  const formData = new FormData();
  formData.append('file', file);
  return axios
    .post(`${PROVIDER_URL}import-staff/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response: AxiosResponse<StaffImportStatus>) => response.data);
};

const getStaffImportStatus = (uuid: string): Promise<StaffImportStatus> => {
  return axios
    .get(`${PROVIDER_URL}import-staff-status/?uuid=${uuid}`)
    .then((response: AxiosResponse<StaffImportStatus>) => response.data);
};

export {
  getProviders,
  getClinicProviders,
  deleteProvider,
  deleteSelectedProviders,
  getProviderById,
  createProvider,
  updateProvider,
  getProviderActivePlans,
  inviteProvider,
  getAccreditations,
  getProviderAccreditationById,
  createProviderAccreditation,
  updateProviderAccreditation,
  createClinic,
  updateClinic,
  togglePaymentFailureNotifications,
  getProviderConnections,
  getConnectionForProvider,
  createProviderConnection,
  updateProviderConnection,
  deleteProviderConnection,
  getSuperbillServices,
  createSuperbillService,
  updateSuperbillService,
  deleteSuperbillService,
  importReferralList,
  getReferralImportStatus,
  getRecentReferralImports,
  importStaffList,
  getStaffImportStatus,
};
